import React, { useEffect } from 'react';
import { render } from 'react-dom';
import browserCheck from '@ublend-npm/browser-check';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { fetchInstitution } from '@core/actions/institutions';
import App from './app';
import { configureStore } from './store';
import ErrorPage from './components/error';

// to be added for error logging
// import ErrorBoundary from '@core/error/ErrorBoundary';
// import {
//   registerGlobalErrorListeners,
//   reportError,
// } from '@core/error/globalErrorListeners';
// import { setupOfflineSync } from '@core/error/syncOfflineErrors';

if (module.hot) {
  module.hot.accept();
}

const defaultAnonymousUserKey = '4382d193-adc7-41ab-9af7-1000a4715915';

const getInstitution = () => {
  const host = window.location.hostname.split('.');

  return process.env.INSTITUTION || (host[0] === 'beta' ? host[1] : host[0]);
};

const getInstitutionData = async (store) => {
  await store.dispatch(fetchInstitution(getInstitution()));

  return store.getState().institution;
};

// to be added for error logging
// registerGlobalErrorListeners();
// setupOfflineSync();

function ViewportDebugPanel() {
  useEffect(() => {
    const panel = document.createElement("div");
    const logs: string[] = [];
    const maxLogs = 12;
    let snapshotCount = 0;

    Object.assign(panel.style, {
      position: "fixed",
      top: "10vh",
      left: "5px",
      right: "5px",
      zIndex: "2147483647",
      background: "rgba(0,0,0,0.95)",
      color: "#00ff90",
      padding: "10px",
      font: "10px/1.4 monospace",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      maxHeight: "70vh",
      overflowY: "auto",
      border: "2px solid #00ff90",
      borderRadius: "8px",
      pointerEvents: "auto",
    });

    const copyButton = document.createElement("button");
    copyButton.textContent = "Copy Logs";

    Object.assign(copyButton.style, {
      position: "sticky",
      top: "0",
      zIndex: "1",
      display: "block",
      width: "100%",
      padding: "10px",
      marginBottom: "8px",
      background: "#00ff90",
      color: "#000",
      border: "0",
      borderRadius: "6px",
      font: "bold 14px sans-serif",
    });

    const output = document.createElement("pre");

    Object.assign(output.style, {
      margin: "0",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      font: "inherit",
    });

    const status = document.createElement("div");
    status.style.marginTop = "8px";
    status.style.color = "#fff";

    panel.append(copyButton, output, status);
    panel.id = "viewport-debug-panel";

    const describe = (el: Element | null) => {
      if (!el) return null;

      const rect = el.getBoundingClientRect();
      const css = getComputedStyle(el);

      return {
        tag: el.tagName,
        className:
          typeof el.className === "string"
            ? el.className.slice(0, 80)
            : "",
        top: +rect.top.toFixed(1),
        bottom: +rect.bottom.toFixed(1),
        height: +rect.height.toFixed(1),
        position: css.position,
        bottomCSS: css.bottom,
        transform: css.transform,
      };
    };

    const snapshot = (event: string) => {
      snapshotCount++;

      const vv = window.visualViewport;

      const data = {
        id: snapshotCount,
        event,
        time: new Date().toISOString(),
        visibilityState: document.visibilityState,

        screen: {
          width: screen.width,
          height: screen.height,
          orientation: screen.orientation?.type,
        },

        window: {
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          scrollY: window.scrollY,
          scrollHeight: document.documentElement.scrollHeight,
          clientHeight: document.documentElement.clientHeight,
        },

        visualViewport: vv
          ? {
              width: +vv.width.toFixed(1),
              height: +vv.height.toFixed(1),
              offsetTop: +vv.offsetTop.toFixed(1),
              pageTop: +vv.pageTop.toFixed(1),
              scale: vv.scale,
            }
          : null,

        root: describe(document.querySelector("#root")),
        body: describe(document.body),

        header: describe(
          document.querySelector("header") ||
            document.querySelector('[role="banner"]')
        ),

        fixedElements: Array.from(
          document.querySelectorAll("body *")
        )
          .filter(
            (el) => getComputedStyle(el).position === "fixed"
          )
          .slice(0, 30)
          .map(describe),
      };

      logs.push(JSON.stringify(data));

      // Keep complete logs for this debugging session.
      output.textContent =
        "VIEWPORT DEBUG — LATEST FIRST\n\n" +
        logs
          .slice()
          .reverse()
          .map((entry) => {
            const data = JSON.parse(entry);
            return `#${data.id} ${data.time} | ${data.event}\n${JSON.stringify(data, null, 2)}`;
          })
          .join("\n\n---\n\n");

      status.textContent = `Snapshots captured: ${snapshotCount}`;
    };

    const copyLogs = async () => {
      const text = logs
        .map((entry) => JSON.parse(entry))
        .map((data) => JSON.stringify(data, null, 2))
        .join("\n\n---\n\n");

      try {
        await navigator.clipboard.writeText(text);
        status.textContent = `Copied ${logs.length} snapshots.`;
      } catch {
        // Fallback for browsers where clipboard access is unavailable.
        const textarea = document.createElement("textarea");
        textarea.value = text;

        Object.assign(textarea.style, {
          position: "fixed",
          top: "0",
          left: "0",
          width: "1px",
          height: "1px",
          opacity: "0.01",
          fontSize: "16px",
        });

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        const success = document.execCommand("copy");
        textarea.remove();

        status.textContent = success
          ? `Copied ${logs.length} snapshots.`
          : "Copy failed. Try selecting the logs manually.";
      }
    };

    copyButton.addEventListener("click", copyLogs);

    const listeners: Array<{
      target: EventTarget;
      event: string;
      callback: EventListener;
    }> = [];

    const listen = (
      target: EventTarget,
      event: string,
      callback: EventListener
    ) => {
      target.addEventListener(event, callback, {
        passive: true,
      });

      listeners.push({ target, event, callback });
    };

    [
      "pageshow",
      "pagehide",
      "orientationchange",
      "focus",
      "blur",
    ].forEach((event) => {
      listen(window, event, () => snapshot(event));
    });

    listen(document, "visibilitychange", () =>
      snapshot("visibilitychange")
    );

    listen(window, "resize", () => {
      snapshot("window.resize - event");

      requestAnimationFrame(() => {
        snapshot("window.resize - next frame");

        requestAnimationFrame(() => {
          snapshot("window.resize - after layout");
        });
      });
    });

    if (window.visualViewport) {
      listen(window.visualViewport, "resize", () =>
        snapshot("visualViewport.resize")
      );

      listen(window.visualViewport, "scroll", () =>
        snapshot("visualViewport.scroll")
      );
    }

    if (document.body) {
      document.body.appendChild(panel);
    }

    snapshot("initial");

    const timer = window.setInterval(
      () => snapshot("periodic"),
      2000
    );

    return () => {
      window.clearInterval(timer);

      listeners.forEach(({ target, event, callback }) => {
        target.removeEventListener(event, callback);
      });

      copyButton.removeEventListener("click", copyLogs);
      panel.remove();
    };
  }, []);

  return null;
}

(async () => {
  // initializeViewportHeightVar();

  if (true) {
    const store = configureStore();
    let institutionData;
    try {
      institutionData = await getInstitutionData(store);
    } catch (error) {
      return render(
        <ErrorPage error={error} />,
        document.getElementById('app')
      );
    }

    const {
      keys: { launchDarkly },
      general: { institution },
    } = institutionData;
    const LDProvider = await asyncWithLDProvider({
      clientSideID: launchDarkly?.clientSideId,
      context: {
        kind: 'user',
        key: defaultAnonymousUserKey,
        institution,
      },
    });

    if (process.env.NODE_ENV === 'local') {
      // eslint-disable-next-line global-require
      const { worker } = require('../test/mocks/browser');
      worker.start({
        onUnhandledRequest: 'bypass',
      });
    }

    render(
      <LDProvider>
        {/*
        to be added for error logging
        <ErrorBoundary
          onError={(err, info) => {
            reportError({
              type: 'react-boundary',
              message: err?.message,
              stack: err?.stack,
              info,
            });
          }}
        >
          <App store={store} institutionData={institutionData} />
        </ErrorBoundary> */}
        <ViewportDebugPanel />
        <App store={store} institutionData={institutionData} />
      </LDProvider>,
      document.getElementById('app')
    );
  } else {
    render(browserCheck.AutoWarning(), document.getElementById('app'));
  }

  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js') // this file should be generated in 'dist' folder after build
        .then((reg) => console.log('SW registered: ', reg))
        .catch((err) => console.error('SW registration failed: ', err));
    });
  }
})();
